import { useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { Navigation } from '@/components/Navigation';
import { BackButton } from '@/components/BackButton';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Coins, TrendingUp, TrendingDown, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { TopUpModal } from '@/components/wallet/TopUpModal';
import { AgeVerification } from '@/components/legal/AgeVerification';

const Wallet = () => {
  const [wallet, setWallet] = useState<any>(null);
  const [transactions, setTransactions] = useState<any[]>([]);
  const [showTopUpModal, setShowTopUpModal] = useState(false);
  const [showAgeVerification, setShowAgeVerification] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingTransactions, setLoadingTransactions] = useState(true);

  useEffect(() => {
    loadWallet();
    loadTransactions();
  }, []);

  const loadWallet = async () => {
    try {
      setLoading(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) return;

      const { data, error } = await supabase
        .from('wallets')
        .select('*')
        .eq('user_id', user.id)
        .single();
      
      if (error) throw error;
      if (data) setWallet(data);
    } catch (error) {
      console.error('Error loading wallet:', error);
      toast.error('Failed to load wallet');
    } finally {
      setLoading(false);
    }
  };

  const loadTransactions = async () => {
    try {
      setLoadingTransactions(true);
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      if (!user) return;

      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(20);
      
      if (error) throw error;
      if (data) setTransactions(data);
    } catch (error) {
      console.error('Error loading transactions:', error);
      toast.error('Failed to load transactions');
    } finally {
      setLoadingTransactions(false);
    }
  };

  // Verify payment on page load if transaction_id exists
  useEffect(() => {
    const verifyPayment = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const transactionId = urlParams.get('transaction_id');
      const status = urlParams.get('status');
      
      if (transactionId && status === 'successful') {
        // Clear URL parameters
        window.history.replaceState({}, '', window.location.pathname);

        try {
          const { data, error } = await supabase.functions.invoke('flutterwave-verify', {
            body: { transaction_id: parseInt(transactionId) },
          });

          if (error) throw error;

          if (data?.success) {
            toast.success(
              `Successfully added ${data.coins_added.toLocaleString()} StageCoins!`,
              {
                description: `New balance: ${data.new_balance.toLocaleString()} coins`,
                duration: 5000,
              }
            );
            loadWallet();
            loadTransactions();
          }
        } catch (error) {
          console.error('Payment verification error:', error);
          toast.error('Payment verification failed', {
            description: 'Please contact support if you were charged.',
          });
        }
      }
    };

    verifyPayment();
  }, []);

  const handleTopUpClick = () => {
    const isVerified = sessionStorage.getItem('age-verified');
    if (!isVerified) {
      setShowAgeVerification(true);
    } else {
      setShowTopUpModal(true);
    }
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navigation />
      
      <div className="container py-8 max-w-4xl flex-1">
        <BackButton to="/" />
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">Wallet</h1>
          <Button onClick={handleTopUpClick} size="lg">
            <Plus className="h-5 w-5 mr-2" />
            Top Up
          </Button>
        </div>

        {/* Balance Overview */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-6">
                  <div className="animate-pulse">
                    <div className="h-4 bg-muted rounded w-24 mb-2" />
                    <div className="h-8 bg-muted rounded w-32" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Current Balance</p>
                    <p className="text-3xl font-bold mt-1">{wallet?.balance?.toLocaleString() || 0}</p>
                  </div>
                  <Coins className="h-8 w-8 text-yellow-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Spent</p>
                    <p className="text-3xl font-bold mt-1">{wallet?.total_spent?.toLocaleString() || 0}</p>
                  </div>
                  <TrendingDown className="h-8 w-8 text-red-500" />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-muted-foreground">Total Earned</p>
                    <p className="text-3xl font-bold mt-1">{wallet?.total_earned?.toLocaleString() || 0}</p>
                  </div>
                  <TrendingUp className="h-8 w-8 text-green-500" />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs defaultValue="buy" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="buy">Buy Coins</TabsTrigger>
            <TabsTrigger value="history">Transaction History</TabsTrigger>
          </TabsList>

          <TabsContent value="buy">
            <Card>
              <CardHeader>
                <CardTitle>Purchase StageCoins</CardTitle>
                <p className="text-sm text-muted-foreground">Support your favorite creators with StageCoins</p>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="text-center py-12">
                  <Coins className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold mb-2">Ready to support creators?</h3>
                  <p className="text-muted-foreground mb-6">
                    Purchase StageCoins to tip your favorite performers and vote on polls
                  </p>
                  <Button size="lg" onClick={handleTopUpClick}>
                    <Plus className="h-5 w-5 mr-2" />
                    Buy StageCoins
                  </Button>
                </div>

                {/* Pricing Info */}
                <div className="border-t pt-6">
                  <h4 className="font-medium mb-3">What can you do with StageCoins?</h4>
                  <div className="space-y-2 text-sm text-muted-foreground">
                    <div className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <p>Send tips to creators during live streams</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <p>Vote on interactive polls and influence the show</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <p>Get recognized in the live chat with highlighted messages</p>
                    </div>
                    <div className="flex items-start gap-2">
                      <span className="text-primary">•</span>
                      <p>Support independent creators directly</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Recent Transactions</CardTitle>
              </CardHeader>
              <CardContent>
                {loadingTransactions ? (
                  <div className="space-y-3">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="animate-pulse">
                        <div className="h-16 bg-muted rounded" />
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {transactions.length === 0 ? (
                      <p className="text-center py-8 text-muted-foreground">
                        No transactions yet
                      </p>
                    ) : (
                      transactions.map((tx) => (
                        <div
                        key={tx.id}
                        className="flex items-center justify-between p-4 border rounded-lg"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`p-2 rounded-full ${
                            tx.type === 'tip' || tx.type === 'vote' 
                              ? 'bg-red-500/10 text-red-500' 
                              : 'bg-green-500/10 text-green-500'
                          }`}>
                            {tx.type === 'tip' || tx.type === 'vote' ? (
                              <TrendingDown className="h-4 w-4" />
                            ) : (
                              <TrendingUp className="h-4 w-4" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium capitalize">{tx.type}</p>
                            <p className="text-xs text-muted-foreground">
                              {formatDate(tx.created_at)}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-bold ${
                            tx.type === 'tip' || tx.type === 'vote'
                              ? 'text-red-500'
                              : 'text-green-500'
                          }`}>
                            {tx.type === 'tip' || tx.type === 'vote' ? '-' : '+'}
                            {tx.amount}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Balance: {tx.balance_after}
                          </p>
                        </div>
                      </div>
                      ))
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Age Verification */}
      <AgeVerification
        open={showAgeVerification}
        onVerified={() => {
          setShowAgeVerification(false);
          setShowTopUpModal(true);
        }}
        onCancel={() => setShowAgeVerification(false)}
      />

      {/* Top-Up Modal */}
      <TopUpModal
        open={showTopUpModal}
        onOpenChange={setShowTopUpModal}
        onSuccess={() => {
          loadWallet();
          loadTransactions();
          setShowTopUpModal(false);
        }}
      />
    </div>
  );
};

export default Wallet;
